/**
 * LossCarryForwardInput — surfaces V2.E1c's
 * `CalculateForYearOptions.previousYearLosses` to the user.
 *
 * Per Art. 150-0 D 12-bis CGI, capital losses can be carried forward
 * up to 10 fiscal years to offset future gains. The user lists each
 * past year's net loss; the calculator filters automatically (the
 * 10-year window + non-positive amounts).
 *
 * Pure presentation — state lives in the parent (TaxCenter). Each
 * row is `{ year, amount }` and the parent persists the array to
 * localStorage. Validation is permissive on input but the calculator
 * filters bad entries silently.
 *
 * Created in Sprint V2.E.UI.2.
 */

import { useState, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';

export interface PreviousYearLossEntry {
  year: number;
  amount: number;
}

interface LossCarryForwardInputProps {
  /** Current list of carry-forward entries. Sorted by the parent
   *  before passing in if order matters for display. */
  losses: PreviousYearLossEntry[];
  /** Called when the user adds, removes or edits an entry. The new
   *  full list is passed (parent replaces, no patches). */
  onChange: (losses: PreviousYearLossEntry[]) => void;
  /** Currently calculated tax year — entries dated in this year or
   *  later are flagged as inadmissible (`< targetYear` check matches
   *  V2.E1c calculator). */
  targetYear: number;
}

const MAX_LOOKBACK_YEARS = 10;

export function LossCarryForwardInput({
  losses,
  onChange,
  targetYear,
}: LossCarryForwardInputProps) {
  const { t } = useTranslation();
  const [draftYear, setDraftYear] = useState<string>(String(targetYear - 1));
  const [draftAmount, setDraftAmount] = useState<string>('');
  const yearInputId = useId();
  const amountInputId = useId();

  const minEligibleYear = targetYear - MAX_LOOKBACK_YEARS;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const year = parseInt(draftYear, 10);
    const amount = parseFloat(draftAmount);
    if (!Number.isFinite(year) || !Number.isFinite(amount) || amount <= 0) return;
    onChange([...losses, { year, amount }]);
    setDraftYear(String(targetYear - 1));
    setDraftAmount('');
  };

  const handleRemove = (index: number) => {
    onChange(losses.filter((_, i) => i !== index));
  };

  const isEligible = (year: number) =>
    year >= minEligibleYear && year < targetYear;

  return (
    <div
      style={{
        padding: '16px',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        background: 'var(--bg-secondary)',
        marginBottom: '20px',
      }}
    >
      <div style={{ marginBottom: '12px' }}>
        <h3
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          {t(
            'taxCenter.carryForward.title',
            'Moins-values reportées des années précédentes',
          )}
        </h3>
        <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0' }}>
          {t(
            'taxCenter.carryForward.help',
            'Art. 150-0 D 12-bis CGI — les moins-values des 10 années précédentes peuvent compenser les plus-values de l\'année courante.',
          )}
        </p>
      </div>

      {losses.length > 0 && (
        <ul
          aria-label={t(
            'taxCenter.carryForward.listLabel',
            'Moins-values déclarées',
          )}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            margin: '0 0 12px',
            padding: 0,
            listStyle: 'none',
          }}
        >
          {losses.map((entry, idx) => {
            const eligible = isEligible(entry.year);
            return (
              <li
                key={`${entry.year}-${idx}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  borderRadius: '12px',
                  background: 'var(--card)',
                  border: `1px solid ${eligible ? 'var(--border)' : 'rgba(239, 68, 68, 0.4)'}`,
                  fontSize: '13px',
                  color: eligible ? 'var(--text)' : 'var(--muted)',
                  textDecoration: eligible ? 'none' : 'line-through',
                }}
                title={
                  eligible
                    ? undefined
                    : t(
                        'taxCenter.carryForward.ineligible',
                        'Hors fenêtre de 10 ans — ignorée par le calcul.',
                      )
                }
              >
                <strong>{entry.year}</strong>
                <span aria-hidden>·</span>
                <span>
                  {entry.amount.toLocaleString('fr-FR', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}{' '}
                  €
                </span>
                <button
                  type="button"
                  onClick={() => handleRemove(idx)}
                  aria-label={t(
                    'taxCenter.carryForward.remove',
                    'Supprimer la perte de {{year}}',
                  ).replace('{{year}}', String(entry.year))}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0 2px',
                    cursor: 'pointer',
                    color: 'var(--muted)',
                    display: 'inline-flex',
                  }}
                >
                  <Icons.X size={11} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <form
        onSubmit={handleAdd}
        style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label
            htmlFor={yearInputId}
            style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 500 }}
          >
            {t('taxCenter.carryForward.year', 'Année')}
          </label>
          <input
            id={yearInputId}
            type="number"
            value={draftYear}
            onChange={(e) => setDraftYear(e.target.value)}
            min={minEligibleYear}
            max={targetYear - 1}
            step={1}
            style={{
              padding: '6px 10px',
              fontSize: '13px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--card)',
              color: 'var(--text)',
              width: '100px',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <label
            htmlFor={amountInputId}
            style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 500 }}
          >
            {t('taxCenter.carryForward.amount', 'Montant (€)')}
          </label>
          <input
            id={amountInputId}
            type="number"
            value={draftAmount}
            onChange={(e) => setDraftAmount(e.target.value)}
            min={0}
            step={0.01}
            placeholder="0.00"
            style={{
              padding: '6px 10px',
              fontSize: '13px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              background: 'var(--card)',
              color: 'var(--text)',
              width: '140px',
            }}
          />
        </div>
        <button
          type="submit"
          disabled={!draftAmount || parseFloat(draftAmount) <= 0}
          style={{
            padding: '7px 14px',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: '6px',
            border: 'none',
            background: 'var(--primary)',
            color: 'white',
            cursor:
              !draftAmount || parseFloat(draftAmount) <= 0
                ? 'not-allowed'
                : 'pointer',
            opacity: !draftAmount || parseFloat(draftAmount) <= 0 ? 0.5 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icons.Plus size={12} />
          {t('taxCenter.carryForward.add', 'Ajouter')}
        </button>
      </form>
    </div>
  );
}
